import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { auth, firestore as db } from '../services/firebase';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';

export interface SubscriptionStatus {
  isActive: boolean;
  planId: string;
  periodEnd?: number;
  periodStart?: number;
  cancelAtPeriodEnd?: boolean;
  priceId?: string;
  status?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  subscriptionStatus: SubscriptionStatus | null;
  subscriptionLoading: boolean;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const defaultSubscription: SubscriptionStatus = {
  isActive: false,
  planId: 'free'
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  subscriptionStatus: defaultSubscription,
  subscriptionLoading: true,
  signInWithGoogle: async () => undefined,
  logout: async () => undefined
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(defaultSubscription);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  // 监听用户身份验证状态
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      setLoading(false);

      if (user) {
        // 创建或更新用户文档
        try {
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);

          if (!userDoc.exists()) {
            await setDoc(userRef, {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL,
              createdAt: new Date(),
            });
          }
        } catch (error) {
          console.error('Error updating user document:', error);
        }
      } else {
        // 用户已登出，重置订阅状态
        setSubscriptionStatus(defaultSubscription);
        setSubscriptionLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // 监听用户订阅状态
  useEffect(() => {
    if (!user) {
      return;
    }

    setSubscriptionLoading(true);

    const subscriptionRef = doc(db, 'users', user.uid, 'subscriptions', 'active');
    const unsubscribe = onSnapshot(
      subscriptionRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setSubscriptionStatus({
            isActive: data.status === 'active' || data.status === 'trialing',
            planId: data.planId || 'free',
            periodEnd: data.current_period_end?.toMillis(),
            periodStart: data.current_period_start?.toMillis(),
            cancelAtPeriodEnd: data.cancel_at_period_end,
            priceId: data.price?.id,
            status: data.status
          });
        } else {
          // 如果没有订阅文档，则设置为免费版
          setSubscriptionStatus(defaultSubscription);
        }
        setSubscriptionLoading(false);
      },
      (error) => {
        console.error('Error getting subscription:', error);
        setSubscriptionStatus(defaultSubscription);
        setSubscriptionLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    subscriptionStatus,
    subscriptionLoading,
    signInWithGoogle,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
